/*
  Invoicing tab (UI overhaul wave 2, screen 07 ideas 4, 5, 6 and 9). Pure helpers, no React.

  isScheduled(invoice)              → a "Not Started" row: a scheduled payment, not an invoice yet
  groupInvoices(invoices, opts)     → "Issued" and "Scheduled · not invoiced yet" groups (filtered by a search)
  validateInvoiceDraft(draft, ctx)  → errors per field, warnings (e.g. the scope would be over-invoiced), the payload
  canSyncToQuickBooks(invoice)      → { ok, reason } for one invoice
  invoicesToCsv(rows)               → Export
*/
import { formatNumber, toNumber } from '@/lib/format';
import { csvRow } from '@/components/documents-ai/csv';
import { parseMoneyInput } from './projectDetailModel';

const text = (value) => String(value ?? '').trim();
const num = (value) => {
  const parsed = toNumber(value);
  return parsed === null ? 0 : parsed;
};
const money = (value) => formatNumber(value, { decimals: 2 });

export const isScheduled = (invoice) => (text(invoice?.status) || 'Not Started') === 'Not Started';

const byReferenceDesc = (a, b) => text(b.reference).localeCompare(text(a.reference), undefined, { numeric: true, sensitivity: 'base' });
const byType = (a, b) => text(a.invoice_type).localeCompare(text(b.invoice_type), undefined, { numeric: true, sensitivity: 'base' });

export function matchesInvoiceSearch(invoice, query) {
  const needle = text(query).toLowerCase();
  if (!needle) return true;
  return [invoice.reference, invoice.note, invoice.invoice_type, invoice.status, invoice.scopeCode]
    .some((value) => text(value).toLowerCase().includes(needle));
}

/** Issued (submitted / paid, newest number first) and Scheduled (not invoiced yet) groups. */
export function groupInvoices(invoices = [], { query = '' } = {}) {
  const rows = (invoices || []).filter((invoice) => matchesInvoiceSearch(invoice, query));
  const issued = rows.filter((invoice) => !isScheduled(invoice)).sort(byReferenceDesc);
  const scheduled = rows.filter(isScheduled).sort(byType);
  const sum = (list) => list.reduce((total, invoice) => total + num(invoice.amount), 0);
  return [
    {
      key: 'issued',
      label: 'Issued',
      meta: `${issued.length} invoice${issued.length === 1 ? '' : 's'}`,
      total: sum(issued),
      rows: issued,
      empty: query ? 'No issued invoice matches.' : 'No invoice has been issued in this scope yet.',
    },
    {
      key: 'scheduled',
      label: 'Scheduled',
      meta: 'not invoiced yet',
      total: sum(scheduled),
      rows: scheduled,
      empty: query ? 'No scheduled payment matches.' : 'Nothing scheduled — every payment of this scope has an invoice.',
    },
  ];
}

export const splitTypes = (value) => [...new Set(text(value).split(',').map((type) => type.trim()).filter(Boolean))];

/**
 * Checks the drawer's form before saving (07 #6).
 * draft: { types: [], percentages: { type: value }, status, amount (typed text), reference, note }
 * ctx:   { scopeLabel, scopeValue, otherTotal (all other invoices of the scope), requireNumber }
 * → { errors: { field: message }, warnings: [message], amount, percentages, ok }
 */
export function validateInvoiceDraft(draft = {}, { scopeLabel = 'this scope', scopeValue = 0, otherTotal = 0, requireNumber = false } = {}) {
  const errors = {};
  const warnings = [];
  const types = draft.types || [];
  if (!types.length) errors.types = 'Pick at least one type';
  let percentages = {};
  if (types.length > 1) {
    let total = 0;
    types.forEach((type) => {
      const value = Number(String(draft.percentages?.[type] ?? '').replace(',', '.'));
      if (!Number.isFinite(value) || value < 0 || String(draft.percentages?.[type] ?? '').trim() === '') {
        errors.percentages = `Enter a percentage for ${type}`;
        return;
      }
      percentages[type] = value;
      total += value;
    });
    if (!errors.percentages && Math.abs(total - 100) > 0.01) errors.percentages = `The percentages add up to ${formatNumber(total, { maxDecimals: 2 })}% — they must make 100%`;
  } else {
    percentages = {};
  }
  const parsed = parseMoneyInput(draft.amount);
  let amount = null;
  if (parsed.error) errors.amount = parsed.error;
  else if (parsed.value < 0) errors.amount = 'The amount cannot be negative';
  else amount = parsed.value;
  const status = text(draft.status) || 'Not Started';
  if (requireNumber && !text(draft.reference)) errors.reference = 'Enter the invoice number';
  if (!requireNumber && status !== 'Not Started' && !text(draft.reference)) warnings.push('No invoice number yet — add it so the invoice can be found and synced.');
  if (amount !== null && scopeValue > 0) {
    const total = num(otherTotal) + amount;
    if (total - scopeValue > 0.005) {
      warnings.push(`Invoices of ${scopeLabel} would add up to ${money(total)} — ${money(total - scopeValue)} more than the scope value ${money(scopeValue)}.`);
    }
  }
  return { errors, warnings, amount, percentages, ok: Object.keys(errors).length === 0 };
}

/**
 * Whether one invoice can be sent to QuickBooks now. status = GET /quickbooks/status ({ configured, connected }):
 * when given, the reason matches the toolbar note — "Not set up" (switched off on this server) vs "Not connected".
 */
export function canSyncToQuickBooks(invoice, { configured = false, status } = {}) {
  if (!configured) {
    if (status && !status.configured) return { ok: false, reason: 'Not set up' };
    if (status && !status.connected) return { ok: false, reason: 'Not connected' };
    return { ok: false, reason: 'QuickBooks is not connected' };
  }
  if (invoice?.quickbooks?.invoice_id) return { ok: false, reason: 'Already synced' };
  if (isScheduled(invoice)) return { ok: false, reason: 'Not invoiced yet' };
  if (num(invoice?.amount) <= 0) return { ok: false, reason: 'Amount is 0' };
  return { ok: true, reason: '' };
}

/**
 * Export (07 #4). Cells go through the app's CSV guard (components/documents-ai/csv.js): quoted when they
 * hold a comma, quote or line break, and a free-text cell starting with = + - @ (an invoice note such as
 * "=HYPERLINK(…)") gets a leading apostrophe so Excel opens it as text instead of running it.
 */
export function invoicesToCsv(rows = [], { scopeValue = 0 } = {}) {
  const header = ['Invoice', 'Scope', 'Type', 'Status', 'Amount', '% of scope', 'Note', 'QuickBooks'];
  const lines = rows.map((invoice) => csvRow([
    invoice.reference || '',
    invoice.scopeCode || 'Base scope',
    invoice.invoice_type || '',
    invoice.status || 'Not Started',
    num(invoice.amount).toFixed(2),
    scopeValue > 0 ? ((num(invoice.amount) / scopeValue) * 100).toFixed(2) : '',
    invoice.note || '',
    invoice.quickbooks?.invoice_id ? `Synced #${invoice.quickbooks.doc_number || invoice.quickbooks.invoice_id}` : (isScheduled(invoice) ? '' : 'Not synced'),
  ]));
  return [csvRow(header), ...lines].join('\n');
}

/* ---------------------------------------------------------------- wave 3 (screen 07 ideas 7 and 8) */

const DAY_MS = 86400000;
const lower = (value) => text(value).toLowerCase();

/**
 * 07 #7 "Ready to bill": which execution main tasks show that a payment term's work is finished. A scheduled
 * invoice of that type is ready when every one of its tasks that exists in the project is Done or Not required
 * and at least one is Done. Advanced payment and Retention never come from execution.
 */
export const BILLING_TRIGGERS = {
  'against delivery': ['material availability'],
  'cable pulling': ['cable pulling'],
  installation: ['devices installation', 'panel termination', 'interfaces installation'],
  't&c': ['cable testing', 'pre-programming', 'soo programming', 'testing & commissioning', 't&c'],
  'handing over': ['handing over documents'],
};

const taskKey = (task) => lower(task?.title || task?.name);
const isDoneStatus = (status) => lower(status) === 'done';
const isNotRequired = (status) => lower(status) === 'not required';

function childProgress(task, tasks) {
  const children = (tasks || []).filter((entry) => entry?.parent_id && entry.parent_id === task.id && !isNotRequired(entry.status));
  if (!children.length) return null;
  return { done: children.filter((entry) => isDoneStatus(entry.status)).length, total: children.length };
}

/** Is the execution work behind one invoice type finished? → { ready, tasks: [{ title, status, done, total }] } | null */
export function billingTriggerState(type, executionTasks = []) {
  const wanted = BILLING_TRIGGERS[lower(type)];
  if (!wanted) return null;
  const main = (executionTasks || []).filter((task) => Number(task?.level || 1) === 1 && wanted.includes(taskKey(task)));
  if (!main.length) return null;
  const ready = main.every((task) => isDoneStatus(task.status) || isNotRequired(task.status)) && main.some((task) => isDoneStatus(task.status));
  // the most telling task first: the first trigger in the list that is Done ("Devices Installation")
  const ordered = [...main].sort((a, b) => wanted.indexOf(taskKey(a)) - wanted.indexOf(taskKey(b)));
  return {
    ready,
    tasks: ordered.map((task) => ({ title: text(task.title || task.name), status: text(task.status) || 'Not Started', ...(childProgress(task, executionTasks) || {}) })),
  };
}

/**
 * Scheduled invoices whose work is done (07 #7), largest first.
 * → [{ invoice, types, amount, share, evidence: { title, done, total } }]
 */
export function findReadyToBill(invoices = [], executionTasks = [], { scopeValue = 0 } = {}) {
  const result = [];
  (invoices || []).filter(isScheduled).forEach((invoice) => {
    const types = splitTypes(invoice.invoice_type);
    if (!types.length) return;
    const states = types.map((type) => billingTriggerState(type, executionTasks));
    if (states.some((state) => !state || !state.ready)) return;
    const evidence = states[0].tasks.find((task) => isDoneStatus(task.status)) || states[0].tasks[0];
    const amount = num(invoice.amount);
    if (amount <= 0) return;
    result.push({ invoice, types, amount, share: scopeValue > 0 ? (amount / scopeValue) * 100 : null, evidence });
  });
  return result.sort((a, b) => b.amount - a.amount);
}

/* 07 #8 — due dates and aging */

export const RETENTION_DLP_DAYS = 365;
const isRetention = (invoice) => splitTypes(invoice?.invoice_type).some((type) => lower(type).includes('retention'));

function dayNumber(value) {
  const raw = text(value);
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return Math.floor(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / DAY_MS);
  const time = Date.parse(raw);
  return Number.isNaN(time) ? null : Math.floor((time + 4 * 3600000) / DAY_MS); // Dubai day of an instant
}

const isoDay = (day) => new Date(day * DAY_MS).toISOString().slice(0, 10);
const todayNumber = (now) => dayNumber(now ? new Date(now).toISOString() : new Date().toISOString());

/** 'YYYY-MM-DD' plus n days. */
export const addDaysIso = (value, days) => {
  const day = dayNumber(value);
  return day === null ? '' : isoDay(day + days);
};

/**
 * When an invoice's money is due and how late it is (07 #8).
 * - Payment Received → { state: 'paid', paidAt, daysToPay }
 * - due_date set     → { state: 'overdue' | 'due', dueDate, days } (days > 0 = late)
 * - Retention not invoiced, no due date → due after the 12-month defects period from the handover date
 *                      ({ source: 'dlp' }). handedOver false (the project is not Completed) and the planned
 *                      handover date already passed → { state: 'handover_pending', source: 'dlp' }: the DLP has
 *                      not started, so there is no due date yet (handedOver undefined = unknown → planned date).
 * - Submitted without a due date → { state: 'open', submittedAt, age, estimated } — submitted_at was recorded from
 *                      wave 2 on; older invoices fall back to their last change (estimated: true, shown "≈", the same
 *                      rule as the Invoicing register).
 * → null when nothing is known.
 */
export function invoiceDueState(invoice, { handoverDate, handedOver, now } = {}) {
  if (!invoice) return null;
  const status = text(invoice.status) || 'Not Started';
  const today = todayNumber(now);
  if (status === 'Payment Received') {
    const paid = dayNumber(invoice.paid_at);
    const submitted = dayNumber(invoice.submitted_at);
    return paid === null ? null : { state: 'paid', paidAt: isoDay(paid), daysToPay: submitted !== null ? Math.max(0, paid - submitted) : null };
  }
  let due = dayNumber(invoice.due_date);
  let source = 'set';
  if (due === null && status === 'Not Started' && isRetention(invoice) && dayNumber(handoverDate) !== null) {
    if (handedOver === false && dayNumber(handoverDate) < today) {
      // handover is late and has not happened: the 12-month defects period has not even started
      return { state: 'handover_pending', source: 'dlp', handoverDate: isoDay(dayNumber(handoverDate)), scheduled: true };
    }
    due = dayNumber(handoverDate) + RETENTION_DLP_DAYS;
    source = 'dlp';
  }
  if (due !== null) {
    const days = today - due;
    return { state: days > 0 ? 'overdue' : 'due', source, dueDate: isoDay(due), days, scheduled: status === 'Not Started' };
  }
  const recorded = dayNumber(invoice.submitted_at);
  const submitted = recorded ?? dayNumber(invoice.updated_at) ?? dayNumber(invoice.created_at);
  if (status === 'Submitted' && submitted !== null) {
    return { state: 'open', submittedAt: isoDay(submitted), age: Math.max(0, today - submitted), estimated: recorded === null };
  }
  return null;
}

/**
 * Collection numbers for the money bar (07 #8):
 *  - overdue receivables — SUBMITTED invoices past their due date (money the client owes now; feeds DSO / aging);
 *  - late to invoice     — scheduled payments past their "invoice by" day and retention past the 12-month DLP:
 *                          not invoiced yet, so not a receivable — counted separately ("1 not invoiced on time");
 *  - DSO — the average days from submission to payment over paid invoices whose two dates are recorded
 *    (weighted by amount).
 */
export function summarizeCollection(invoices = [], { handoverDate, handedOver, now } = {}) {
  let overdueCount = 0;
  let overdueAmount = 0;
  let lateToInvoiceCount = 0;
  let lateToInvoiceAmount = 0;
  let paidWeight = 0;
  let paidDays = 0;
  let paidCount = 0;
  let oldest = null;
  let raised = 0;
  (invoices || []).forEach((invoice) => {
    if (!isScheduled(invoice)) raised += 1;
    const state = invoiceDueState(invoice, { handoverDate, handedOver, now });
    if (!state) return;
    if (state.state === 'overdue' && state.scheduled) {
      lateToInvoiceCount += 1;
      lateToInvoiceAmount += num(invoice.amount);
    } else if (state.state === 'overdue') {
      overdueCount += 1;
      overdueAmount += num(invoice.amount);
      if (oldest === null || state.days > oldest) oldest = state.days;
    }
    if (state.state === 'paid' && state.daysToPay !== null) {
      const weight = Math.max(num(invoice.amount), 0.01);
      paidWeight += weight;
      paidDays += state.daysToPay * weight;
      paidCount += 1;
    }
  });
  return {
    overdueCount,
    overdueAmount,
    oldestOverdueDays: oldest,
    lateToInvoiceCount,
    lateToInvoiceAmount,
    dso: paidCount ? Math.round(paidDays / paidWeight) : null,
    dsoBasis: paidCount,
    raised,
  };
}
